import { useCallback, useMemo } from "react";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { Resolver, useForm } from "react-hook-form";
import { z } from "zod";

import { useCurrentPatientQuery } from "@/data/queries/patientsQueries";
import {
  homeAddressRefinement,
  householdMemberFormBaseSchema,
  householdMemberFormSchema,
  HouseholdMemberFormType,
  mailingAddressRefinement,
  requiredEmailSchema,
  validateHomeAddress,
  validateMailingAddress,
  validateOtherRelationship,
} from "@/domain/householdMember";

export const PROFILE_INFORMATION_FORM_DEFAULT_VALUES: Partial<HouseholdMemberFormType> =
  {
    dateOfBirth: undefined,
    shouldUseDifferentMailingAddress: false,
    hasNoMailingAddress: false,
    isHomeless: false,
    phoneNumber: "",
    ssn: "",
  };

export const useProfileInformationForm = ({
  isPrimaryMember,
}: {
  isPrimaryMember: boolean;
}) => {
  // Read through the query, not the context, which is absent during enrolment.
  const { data: patient } = useCurrentPatientQuery();
  const isEmailRequired = !patient?.username;

  const defaultResolver = useMemo(() => {
    const primaryMemberSchema = isEmailRequired
      ? householdMemberFormBaseSchema
          .extend({ email: requiredEmailSchema })
          .superRefine(validateOtherRelationship)
          .refine(validateHomeAddress, homeAddressRefinement)
          .refine(validateMailingAddress, mailingAddressRefinement)
      : householdMemberFormSchema;

    const schema = isPrimaryMember
      ? primaryMemberSchema
      : householdMemberFormBaseSchema
          .omit({ phoneNumber: true })
          .extend({ phoneNumber: z.string().optional() })
          .superRefine(validateOtherRelationship)
          .refine(validateHomeAddress, homeAddressRefinement)
          .refine(validateMailingAddress, mailingAddressRefinement);

    return standardSchemaResolver(schema) as Resolver<HouseholdMemberFormType>;
  }, [isPrimaryMember, isEmailRequired]);

  const customResolver = useCallback(
    async (...args: Parameters<Resolver<HouseholdMemberFormType>>) => {
      return await defaultResolver(...args);
    },
    [defaultResolver],
  );

  const form = useForm<HouseholdMemberFormType>({
    defaultValues: PROFILE_INFORMATION_FORM_DEFAULT_VALUES,
    resolver: customResolver,
  });

  const { control, watch, setValue, reset, formState } = form;

  return {
    form,
    control,
    watch,
    setValue,
    reset,
    formState,
  };
};
